export * from './contrast'
export * from './management'
export { normalizeColor } from './normalize'
export * from './okhcl'

import { parse, formatHex, formatHex8, formatRgb, converter, differenceEuclidean } from 'culori'

import { BLACK } from '../constants'
import type { Color } from '../primitives'

const toRGB = converter('rgb')

/** A CSS color, or null when `input` is not one. */
export function tryParseColor(input: string): Color | null {
  const parsed = parse(input)
  if (!parsed) return null
  const rgb = toRGB(parsed)
  return {
    r: rgb.r,
    g: rgb.g,
    b: rgb.b,
    a: parsed.alpha ?? 1
  }
}

/** A CSS color, or black when `input` is not one. */
export function parseColor(input: string): Color {
  return tryParseColor(input) ?? { ...BLACK }
}

export function colorToHex(color: Color): string {
  return formatHex({ mode: 'rgb', r: color.r, g: color.g, b: color.b }).toUpperCase()
}

export function colorToHex8(color: Color, alpha?: number): string {
  const a = alpha ?? color.a
  if (a >= 1) return colorToHex(color)
  return formatHex8({ mode: 'rgb', r: color.r, g: color.g, b: color.b, alpha: a }).toUpperCase()
}

export function colorToHexRaw(color: Color): string {
  return colorToHex(color).slice(1)
}

export function colorToRgba255(color: Color) {
  return {
    r: Math.round(color.r * 255),
    g: Math.round(color.g * 255),
    b: Math.round(color.b * 255),
    a: color.a
  }
}

export function colorToCSS(color: Color): string {
  return formatRgb({ mode: 'rgb', r: color.r, g: color.g, b: color.b, alpha: color.a })
}

export function colorToCSSCompact(color: Color): string {
  const { r, g, b } = colorToRgba255(color)
  const a = Number(color.a.toFixed(3))
  if (a >= 1) return `rgb(${r},${g},${b})`
  return `rgba(${r},${g},${b},${a})`
}

export function rgba255ToColor(r: number, g: number, b: number, a = 1): Color {
  return { r: r / 255, g: g / 255, b: b / 255, a }
}

export function colorToFill(color: string | Color) {
  const rgba = typeof color === 'string' ? parseColor(color) : color
  return {
    type: 'SOLID' as const,
    color: { r: rgba.r, g: rgba.g, b: rgba.b, a: rgba.a },
    opacity: rgba.a,
    visible: true
  }
}

const euclideanRGB255 = differenceEuclidean('rgb')

export function colorDistance(c1: Color, c2: Color): number {
  return (
    euclideanRGB255(
      { mode: 'rgb', r: c1.r, g: c1.g, b: c1.b },
      { mode: 'rgb', r: c2.r, g: c2.g, b: c2.b }
    ) * 255
  )
}
